import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Bell, Search, ArrowDown, Globe, ArrowUp, CreditCard, MoreHorizontal } from 'lucide-react';
import { Screen } from '../../types';
import { PhoneFrame } from '../PhoneFrame';
import PkoLogo from '../../assets/pko.svg';
import EthLogo from '../../assets/ethereum-eth-logo.svg';
import Group10Logo from '../../assets/Group 10.svg';
import PolskiShield from '../../assets/Polski-shield.svg';
import EU from '../../assets/EU.svg';


interface ConclusionScreenProps {
  screen: Screen;
}

export const ConclusionScreen: React.FC<ConclusionScreenProps> = () => {
  const renderHomeScreenContent = () => (
    <div className="p-6 h-full flex flex-col">
      {/* Top Navigation */}
      <div className="flex items-center justify-between mb-6">
        <ArrowLeft className="w-5 h-5 text-gray-600" />
        <div className="flex items-center space-x-3">
          <Bell className="w-5 h-5 text-gray-600" />
          <Search className="w-5 h-5 text-gray-600" />
        </div>
      </div>

      {/* Primary Account Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="bg-blue-50 rounded-lg p-4 mb-6"
      >
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-semibold text-gray-900">Primary Account</h2>
        </div>
        
        <div className="text-3xl font-bold text-gray-900 mb-2">$500.45</div>
        
        <div className="text-sm text-gray-600 mb-3">Balance</div>
        
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">USDC Rewards (4.0% APY)</span>
          <span className="text-sm text-green-600 font-medium">$0.12</span>
        </div>
      </motion.div>

      {/* Action Buttons */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
        className="grid grid-cols-5 gap-4 mb-6"
      >
        {[
          { name: "Virtual Account", icon: ArrowDown },
          { name: "Wallet", icon: Globe },
          { name: "Pay", icon: ArrowUp },
          { name: "Card", icon: CreditCard },
          { name: "More", icon: MoreHorizontal }
        ].map((action) => {
          return (
            <motion.div
              key={action.name}
              className="flex flex-col items-center space-y-2 p-2"
            >
              <div className="w-12 h-12 rounded-full flex items-center justify-center bg-gray-100">
                <action.icon className="w-6 h-6 text-gray-600" />
              </div>
              <span className="text-xs text-center text-gray-600">
                {action.name}
              </span>
            </motion.div>
          );
        })}
      </motion.div>

      {/* Transaction History */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.6 }}
        className="flex-1"
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Transaction History</h3>
        
        {/* Recent Transactions */}
        <div className="space-y-3">

          {/* PLN Withdrawal to PKO Bank Polski */}
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center">
                <img src={PkoLogo} alt="PKO" className="w-8 h-8" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-medium text-gray-900">PKO Bank Polski</div>
              <div className="text-xs text-gray-500">Elixir Express</div>
            </div>
            <div className="text-sm font-semibold text-red-600">- 50 zł</div>
          </div>

          {/* ETH Deposit from Vitalik Buterin */}
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-white-500 rounded-full flex items-center justify-center">
              <img src={EthLogo} alt="ETH" className="w-8 h-8" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-medium text-gray-900">Vitalik Buterin</div>
              <div className="text-xs text-gray-500">ETH Deposit</div>
            </div>
            <div className="text-sm font-semibold text-green-600">+ 0.1 ETH</div>
          </div>

          {/* Original SEPA Transaction */}
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                <img src={PkoLogo} alt="PKO" className="w-8 h-8" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-medium text-gray-900">PKO Bank Polski</div>
              <div className="text-xs text-gray-500">SEPA Instant</div>
            </div>
            <div className="text-sm font-semibold text-green-600">+ €100.00</div>
          </div>
        </div>
      </motion.div>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center px-8 py-16">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        {/* Left Side - Mobile Interface */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="flex justify-center"
        >
          <PhoneFrame>
            {renderHomeScreenContent()}
          </PhoneFrame>
        </motion.div>

        {/* Right Side - Content */}
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="space-y-6"
        >
          <div>
            {/* Logo above title */}
            <div className="mb-6">
            <img src={Group10Logo} alt="Across Logo" className="h-24 w-auto" />
            </div>
            
            <h2 className="text-3xl font-bold text-black mb-4">
              Build with Across
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Across lets your business send, receive, and convert funds between fiat and stablecoins instantly — no new licenses or infrastructure required. <br /> Talk to our team to explore integration for your payment platform.
            </p>
            
            {/* Book a Call button */}
            <button
              onClick={() => window.open('https://calendar.app.google/GiRdjUKPwKyBEj5c9', '_blank', 'noopener,noreferrer')}
              className="bg-[#00A19B] hover:bg-[#008F89] text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200"
            >
              Book a Call
            </button>
            
            {/* Regulatory Text and Images */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="flex items-center space-x-4">
                <img src={PolskiShield} alt="Polski Shield" className="h-8 w-auto" />
                <img src={EU} alt="EU" className="h-8 w-auto" />
              </div>
              <p className="text-xs text-gray-500 mt-3 leading-relaxed">
                Across API is a service provided by Baltic Block Sp.z o.o., a company registered in Poland with company no. 0001076720 (KRS) and authorized and regulated by the Ministry of Finance, specifically through the Tax Administration Chamber in Katowice (registration no. RDWW-1105), as a virtual asset service provider.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};
